import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { notify } from '@/lib/notify';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, MoreHorizontal, Eye, Pencil, UserCheck, Pause, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TableLink } from '@/components/global';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PlatformHeader, MetricCard, StatusBadge, TierBadge } from '@/components/platform';

type SubscriptionStatus = 'ACTIVE' | 'TRIAL' | 'SUSPENDED' | 'CANCELLED';
type SubscriptionTier = 'FREE' | 'STANDARD' | 'PREMIUM' | 'ENTERPRISE';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  subscriptionTier: SubscriptionTier;
  subscriptionStatus: SubscriptionStatus;
  userCount: number;
  machineCount: number;
  maxUsers: number;
  maxMachines: number;
  createdAt: string;
}

interface TenantsResponse {
  items: Tenant[];
  total: number;
  page: number;
  pageSize: number;
}

export default function TenantsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<TenantsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || '10', 10);
  const search = searchParams.get('search') || '';
  const status = searchParams.get('status') as SubscriptionStatus | undefined;
  const tier = searchParams.get('tier') as SubscriptionTier | undefined;

  const loadTenants = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('pageSize', pageSize.toString());
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      if (tier) params.set('tier', tier);

      const res = await apiFetch(`/api/v1/platform/tenants?${params}`);
      if (!res.ok) {
        if (res.status === 403) {
          setError('Access denied. Platform admin role required.');
          return;
        }
        throw new Error('Failed to load tenants');
      }
      const result = await res.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, status, tier]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const updateSearch = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set('search', value);
    } else {
      params.delete('search');
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const updateFilter = (key: string, value: string | undefined) => {
    const params = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.set('page', '1');
    setSearchParams(params);
  };

  const changePage = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const handleSuspend = async (tenant: Tenant) => {
    if (!window.confirm(`Are you sure you want to suspend "${tenant.name}"?`)) return;
    try {
      const res = await apiFetch(`/api/v1/platform/tenants/${tenant.id}/suspend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Suspended via platform admin' }),
      });
      if (res.ok) {
        loadTenants();
      }
    } catch {
      notify.error('Failed to suspend tenant');
    }
  };

  const handleActivate = async (tenant: Tenant) => {
    try {
      const res = await apiFetch(`/api/v1/platform/tenants/${tenant.id}/activate`, {
        method: 'POST',
      });
      if (res.ok) {
        loadTenants();
      }
    } catch {
      notify.error('Failed to activate tenant');
    }
  };

  const totalPages = data ? Math.ceil(data.total / data.pageSize) : 1;
  const statusCounts = {
    active: data?.items.filter(t => t.subscriptionStatus === 'ACTIVE').length || 0,
    trial: data?.items.filter(t => t.subscriptionStatus === 'TRIAL').length || 0,
    suspended: data?.items.filter(t => t.subscriptionStatus === 'SUSPENDED').length || 0,
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PlatformHeader />
        <div className="flex flex-col items-center justify-center gap-4 p-8">
          <p className="text-red-600">{error}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PlatformHeader />
      <div className="space-y-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Tenants</h1>
            <p className="text-muted-foreground">Manage tenant organizations</p>
          </div>
          <Button asChild>
            <Link to="/platform/tenants/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Tenant
            </Link>
          </Button>
        </div>

        <div className="mb-6 grid gap-4 md:grid-cols-4">
          <MetricCard title="Total Tenants" value={data?.total || 0} />
          <MetricCard title="Active" value={statusCounts.active} />
          <MetricCard title="On Trial" value={statusCounts.trial} />
          <MetricCard title="Suspended" value={statusCounts.suspended} />
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="relative flex-1 md:max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by name or slug..."
                  value={search}
                  onChange={(e) => updateSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex gap-2">
                <Select value={status || 'all'} onValueChange={(v) => updateFilter('status', v)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="TRIAL">Trial</SelectItem>
                    <SelectItem value="SUSPENDED">Suspended</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={tier || 'all'} onValueChange={(v) => updateFilter('tier', v)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tiers</SelectItem>
                    <SelectItem value="FREE">Free</SelectItem>
                    <SelectItem value="STANDARD">Standard</SelectItem>
                    <SelectItem value="PREMIUM">Premium</SelectItem>
                    <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-muted-foreground">Loading...</p>
              </div>
            ) : (
              <>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tenant</TableHead>
                        <TableHead>Slug</TableHead>
                        <TableHead>Tier</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Users</TableHead>
                        <TableHead>Machines</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data?.items.map((tenant) => (
                        <TableRow key={tenant.id}>
                          <TableCell>
                            <TableLink to={`/platform/tenants/${tenant.id}`}>
                              {tenant.name}
                            </TableLink>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {tenant.slug}
                          </TableCell>
                          <TableCell>
                            <TierBadge tier={tenant.subscriptionTier} />
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={tenant.subscriptionStatus} />
                          </TableCell>
                          <TableCell>
                            {tenant.userCount} / {tenant.maxUsers}
                          </TableCell>
                          <TableCell>
                            {tenant.machineCount} / {tenant.maxMachines}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {new Date(tenant.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem asChild>
                                  <Link to={`/platform/tenants/${tenant.id}`}>
                                    <Eye className="mr-2 h-4 w-4" />
                                    View
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                  <Link to={`/platform/tenants/${tenant.id}/edit`}>
                                    <Pencil className="mr-2 h-4 w-4" />
                                    Edit
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                  <Link to={`/platform/tenants/${tenant.id}?impersonate=true`}>
                                    <UserCheck className="mr-2 h-4 w-4" />
                                    Impersonate
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {tenant.subscriptionStatus === 'SUSPENDED' ? (
                                  <DropdownMenuItem onClick={() => handleActivate(tenant)}>
                                    <Play className="mr-2 h-4 w-4" />
                                    Activate
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem
                                    className="text-red-600"
                                    onClick={() => handleSuspend(tenant)}
                                  >
                                    <Pause className="mr-2 h-4 w-4" />
                                    Suspend
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                      {data?.items.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                            No tenants found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages} ({data?.total || 0} total)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => changePage(page - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages}
                      onClick={() => changePage(page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
